import {
	ConfirmationDialog,
	ContextMenu,
	ContextMenuItem,
	ContextMenuLinkItem,
	IDataListColumn,
	IconButton,
	Id,
	Loading,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
} from "@dgs/core";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContactFilterApplyDrawer, ContactFilterBar } from "../../../contactFilter";
import { useContactFilter } from "../../../contactFilter/useContactFilter";
import { useContactListUtils } from "../../../contacts/useContactListUtils";
import { PendingFeedbackContactsBulkEditAction } from "../../../contactUpdateInquiries/details/contactLists/PendingFeedbackContactsBulkEditAction";
import { useContactUpdateInquiryContactActions } from "../../../contactUpdateInquiries/details/contactLists/useContactUpdateInquiryContactActions";
import { LocalContactViewSwitcher } from "../../../contactViews/local/LocalContactViewSwitcher";
import { contactUpdateInquiryService } from "../../../shared/api/contactUpdateInquiryService";
import { useContactView } from "../../../shared/hooks/useContactView";
import { IContactIndexResource } from "../../../shared/types";
import { ContactUpdateInquiryContactIndexResource } from "../../../shared/types/contactUpdateInquiry";

interface Props {
	contactUpdateInquiryId: Id;
}

export const PendingFeedbackContactList: FC<Props> = ({ contactUpdateInquiryId }) => {
	const { t } = useTranslation();
	const { currentContactView, isLoading: contactViewLoading } = useContactView();
	const { columns } = useContactListUtils(currentContactView);
	const { onApplyContactFilter, openContactFilterDrawer, activeContactFilterId, applyFilterDrawerProps } =
		useContactFilter(contactUpdateInquiryService.contactsWithPendingFeedbackKey(contactUpdateInquiryId));
	const { closePendingContactUpdateInquiry, resendContactInquiryEmail } = useContactUpdateInquiryContactActions(
		contactUpdateInquiryId,
		activeContactFilterId,
	);
	const [contactId, setContactId] = useState<Id | null>(null);

	const queryKey = useMemo(() => {
		const baseKey = [
			...contactUpdateInquiryService.contactsWithPendingFeedbackKey(contactUpdateInquiryId),
			contactUpdateInquiryService.contactsWithInquiryKeySuffix,
			`${activeContactFilterId}`,
		];

		if (currentContactView?.id) {
			return baseKey.concat(`${currentContactView.id}`);
		}

		return baseKey;
	}, [activeContactFilterId, contactUpdateInquiryId, currentContactView]);

	const dataListColumns: IDataListColumn<IContactIndexResource>[] = useMemo(
		() => [
			...columns,
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (contact) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Resend contact update inquiry")}
							label={t("Resend contact update inquiry")}
							action={() => setContactId(contact.id)}
							icon="send"
							shortcut="primary"
						/>
						<ContextMenuLinkItem
							to={`/contacts/active/${contact.id}`}
							title={t("View contact")}
							label={t("View contact")}
							icon="eye"
							shortcut="default"
						/>
						<ContextMenuItem
							title={t("Mark as completed")}
							label={t("Mark as completed")}
							action={() => closePendingContactUpdateInquiry([contact.id])}
							icon="check"
						/>
					</ContextMenu>
				),
			},
		],
		[closePendingContactUpdateInquiry, columns, t],
	);

	const initialSortingAttribute =
		currentContactView?.contactAttributes.find((attribute) => attribute.entityType === "lastName") ??
		currentContactView?.contactAttributes.find((attribute) => attribute.entityType === "createdAt");

	if (contactViewLoading) {
		return <Loading />;
	}

	return (
		<>
			<PaginatedAutoDataList
				name="pendingFeedbacks"
				empty={t("No feedbacks available.")}
				headerChildren={
					<ContactFilterBar
						contactFilterId={activeContactFilterId}
						onApply={onApplyContactFilter}
						onClick={openContactFilterDrawer}
					/>
				}
				heading={t("Pending feedback")}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(
					queryKey,
					(config: PaginatedListFetcherConfig<ContactUpdateInquiryContactIndexResource>) =>
						contactUpdateInquiryService.contactsWithPendingFeedback(
							config,
							activeContactFilterId,
							currentContactView?.id || null,
							contactUpdateInquiryId,
						),
				)}
				headerActions={
					<>
						<LocalContactViewSwitcher />
						<IconButton size="small" title={t("Contact filter")} icon="filter" onClick={openContactFilterDrawer} />
					</>
				}
				initialSearch={{
					search: "",
					searchFields: [
						{ name: "firstName", label: t("First name") },
						{ name: "lastName", label: t("Last name") },
						{ name: "email", label: t("Email") },
						{ name: "phone", label: t("Phone") },
						{ name: "company", label: t("Company") },
					],
				}}
				enableBulkEdit={true}
				bulkEditActions={
					<PendingFeedbackContactsBulkEditAction
						contactUpdateInquiryId={contactUpdateInquiryId}
						activeContactFilterId={activeContactFilterId}
					/>
				}
				initialSorting={
					initialSortingAttribute ? { column: initialSortingAttribute.id as any, direction: "asc" } : null
				}
				onRowClick={({ id }) => setContactId(id)}
			/>
			{applyFilterDrawerProps.editEntityId != null && (
				<ContactFilterApplyDrawer
					{...applyFilterDrawerProps}
					contactFilterId={activeContactFilterId}
					onApply={onApplyContactFilter}
				/>
			)}
			<ConfirmationDialog
				open={!!contactId}
				onClose={() => setContactId(null)}
				action={() => {
					contactId && resendContactInquiryEmail([contactId]);
					setContactId(null);
				}}
				heading={t("Pending feedback")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
			>
				{t("Are you sure you want to resend the contact update inquiry?")}
			</ConfirmationDialog>
		</>
	);
};
